import React from 'react';
import { useMarineSedimentPollutionIndexMetricsQuery } from 'src/app/dashboard/state/metrics/metricsGraphSlice';
import Loader from 'src/shared/components/loader/Loader';
import useProject from 'src/shared/hooks/useProject';
import mspiImageUrl from 'src/assets/images/dashboard/mspi.webp';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import Chip from 'src/shared/components/chip/Chip';
import TimeseriesCardTemplate from '../components/TimeseriesCardTemplate';
import styles from '../Metrics.module.scss';

const getMspiChipDetails = (value: number) => {
    let label = '';
    let colorClassNames = '';

    if (value < 1.3) {
        label = 'Unpolluted';
        colorClassNames = 'text-green-100 bg-green-0';
    } else if (value < 3.4) {
        label = 'Slightly polluted';
        colorClassNames = 'text-green-100 bg-green-0';
    } else if (value < 5) {
        label = 'Moderately polluted';
        colorClassNames = 'text-orange-100 bg-orange-0';
    } else if (value < 6) {
        label = 'Heavily polluted';
        colorClassNames = 'text-red-100 bg-red-0';
    } else {
        label = 'Extremely polluted';
        colorClassNames = 'text-red-100 bg-red-0';
    }

    return { colorClassNames, label };
};

const MarineSedimentPollutionIndexMetrics = () => {
    const chartTitle = 'Marine Sediment Pollution Index';
    const {
        currentProjectSelectedDateRange,
        currentProjectId,
        currentProjectSelectedHabitatAssayType: habitatAssayType,
    } = useProjectFilters();

    const { isSingleTimepoint } = useProject();

    const { currentData, isFetching } = useMarineSedimentPollutionIndexMetricsQuery(
        {
            projectId: currentProjectId || '',
            fromDate: currentProjectSelectedDateRange?.startDate,
            toDate: currentProjectSelectedDateRange?.endDate,
            habitatAssay: habitatAssayType?.key || '',
        },
        {
            skip: !currentProjectId || !habitatAssayType,
        }
    );

    if (isFetching) {
        return <Loader />;
    }

    if (!currentData) {
        return null;
    }

    const mspiChipDetails = getMspiChipDetails(currentData.marineSedimentPollutionIndex?.data?.[0]?.data?.[0]?.group?.[0]?.value || 0);
    const mspiChip = <Chip className={`px-2 py-1 text-xs rounded-2xl ${mspiChipDetails.colorClassNames}`} label={mspiChipDetails.label} />;

    const content = (
        <TimeseriesCardTemplate
            title={chartTitle}
            titleHelpSlug='MarineSedimentPollutionIndex'
            averageValueDescription='Average index per sample [endYearWithQuarter]'
            metricsChartData={currentData.marineSedimentPollutionIndex}
            metricCardImageUrl={mspiImageUrl}
            metricCardContent='average index per sample'
            averageValueChip={mspiChip}
        />
    );

    if (isSingleTimepoint) {
        return content;
    }

    return <div className={styles.timeseriesCard}>{content}</div>;
};

export default MarineSedimentPollutionIndexMetrics;
